// Shared client helper for resolving a fresh signed URL for a wardrobe item
// image stored in the private `outfits` bucket. Re-signs on every read so
// expired links (default 7d at upload time) auto-heal. Lazily backfills
// `image_path` for legacy rows.

import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase as defaultClient } from "@/integrations/supabase/client";

export const WARDROBE_SIGNED_URL_EXPIRY_SECONDS = 60 * 60; // 1 hour — refreshed on every component mount

export interface WardrobeItemRef {
  id: string;
  image_url: string | null;
  image_path: string | null;
  cutout_path?: string | null;
  cutout_url?: string | null; // deprecated; retained for backwards-compat
  cutout_status?: string | null;
}

export interface SignedImageResult {
  url: string | null;
  path: string | null;
  refreshed: boolean;
  error?: string;
}

/**
 * Extract the storage path portion from a Supabase signed URL for the
 * `outfits` bucket. Returns null for unrelated URLs (e.g. external thumbnails
 * imported from retailer pages).
 */
export function extractOutfitsPath(url: string | null): string | null {
  if (!url) return null;
  const match = url.match(/\/object\/sign\/outfits\/([^?]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Returns a fresh signed URL for a wardrobe item's image.
 * - Prefers stored `image_path`.
 * - Falls back to extracting the path from a legacy signed `image_url`.
 * - Returns the original `image_url` unchanged when no storage path is
 *   resolvable (e.g. external retailer thumbnail imported from past outfit).
 * - Lazily backfills `image_path` on the row when missing.
 */
export async function getWardrobeImageUrl(
  item: WardrobeItemRef,
  client: SupabaseClient = defaultClient,
  options: { skipPathBackfill?: boolean } = {},
): Promise<SignedImageResult> {
  // Prefer cutout_path when the cutout is ready.
  const cutoutReady =
    item.cutout_status === "ready" && !!item.cutout_path;
  let path: string | null = cutoutReady
    ? item.cutout_path!
    : (item.image_path ?? null);
  if (!path && !cutoutReady) path = extractOutfitsPath(item.image_url);

  if (!path) {
    return {
      url: item.image_url ?? null,
      path: null,
      refreshed: false,
      error: item.image_url ? undefined : "no_storage_path_resolvable",
    };
  }

  const { data, error } = await client.storage
    .from("outfits")
    .createSignedUrl(path, WARDROBE_SIGNED_URL_EXPIRY_SECONDS);

  if (error || !data?.signedUrl) {
    return {
      url: item.image_url ?? null,
      path,
      refreshed: false,
      error: error?.message ?? "sign_failed",
    };
  }

  if (!cutoutReady && !item.image_path && !options.skipPathBackfill && item.id) {
    void client
      .from("wardrobe_items")
      .update({ image_path: path })
      .eq("id", item.id)
      .then(({ error: updateError }) => {
        if (updateError) {
          console.warn(
            `[wardrobe-image] lazy backfill failed for ${item.id}:`,
            updateError.message,
          );
        }
      });
  }

  return { url: data.signedUrl, path, refreshed: true };
}

export async function getWardrobeImageUrls(
  items: WardrobeItemRef[],
  client: SupabaseClient = defaultClient,
): Promise<Map<string, SignedImageResult>> {
  const results = new Map<string, SignedImageResult>();
  await Promise.all(
    items.map(async (item) => {
      const r = await getWardrobeImageUrl(item, client);
      results.set(item.id, r);
    }),
  );
  return results;
}

/**
 * React hook variant. Returns the resolved URL (null while pending or on
 * unrecoverable failure). Re-runs when the item identity or stored fields
 * change.
 */
export function useWardrobeImageUrl(
  item:
    | Pick<
        WardrobeItemRef,
        | "id"
        | "image_url"
        | "image_path"
        | "cutout_path"
        | "cutout_url"
        | "cutout_status"
      >
    | null
    | undefined,
): string | null {
  // Prefer cutout_path when ready; signed on read like image_path.
  const cutoutReady =
    item?.cutout_status === "ready" && !!item?.cutout_path;
  // When a cutout is ready we MUST NOT seed with the raw image_url —
  // it would flash the un-cut original through transparent regions while
  // the signed cutout URL is being fetched. Seed null in that case so the
  // parent's surface-2 placeholder shows until the cutout URL resolves.
  const [url, setUrl] = useState<string | null>(
    cutoutReady ? null : (item?.image_url ?? null),
  );

  useEffect(() => {
    if (!item) {
      setUrl(null);
      return;
    }
    // Same anti-flash rule on identity change: only show raw image_url
    // optimistically when there is no ready cutout to wait for.
    setUrl(cutoutReady ? null : (item.image_url ?? null));
    let cancelled = false;
    void getWardrobeImageUrl({
      id: item.id,
      image_url: item.image_url ?? null,
      image_path: item.image_path ?? null,
      cutout_path: item.cutout_path ?? null,
      cutout_status: item.cutout_status ?? null,
    }).then((r) => {
      if (!cancelled) setUrl(r.url);
    });
    return () => {
      cancelled = true;
    };
  }, [
    item?.id,
    item?.image_url,
    item?.image_path,
    item?.cutout_path,
    item?.cutout_status,
    cutoutReady,
  ]);

  return url;
}


/**
 * Transform a Supabase storage URL to use the on-the-fly image transform
 * endpoint. Pass-through for non-Supabase URLs.
 */
export function withRenderTransform(
  url: string | null | undefined,
  width: number,
  quality = 80,
): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("supabase.co")) return url;
    if (parsed.pathname.includes("/object/sign/")) {
      const p = parsed.pathname.replace("/object/sign/", "/render/image/sign/");
      return `${parsed.origin}${p}${parsed.search}&width=${width}&quality=${quality}&resize=contain`;
    }
    if (parsed.pathname.includes("/object/public/")) {
      const p = parsed.pathname.replace("/object/public/", "/render/image/public/");
      return `${parsed.origin}${p}?width=${width}&quality=${quality}&resize=contain`;
    }
    return url;
  } catch {
    return url;
  }
}
